// @ts-ignore
import { marked } from "marked";
import { sanitize } from 'dompurify';

// https://jsfiddle.net/api/post/jquery/1.12/?example=custom-maps-layer-(open-street-map)
// https://gist.github.com/BrendonKoz/b1df234fe3ee388b402cd8e98f7eedbd
// https://cartodb-basemaps-{s}.global.ssl.fastly.net/light_all/{z}/{x}/{y}.png

// CARTO now requires an API key on every basemap request, otherwise tiles are
// served with a watermark. The key is baked in at build time by CRA, so it must
// be provided when building the image, not when running the container.
// https://carto.com/basemaps/apikey/
const CARTO_BASEMAP_KEY = process.env.REACT_APP_CARTO_BASEMAP_KEY;

function cartoBasemapUrl(style) {
  const url = `https://{s}.basemaps.cartocdn.com/${style}/{z}/{x}/{y}.png`;

  if (!CARTO_BASEMAP_KEY) {
    // no key: tiles still render, but watermarked
    return url;
  }

  return `${url}?key=${encodeURIComponent(CARTO_BASEMAP_KEY)}`;
}

function loadThemedTiledLayer(initial_theme) {
  let current_theme = null;
  let layers = null;

  const ensureLayers = () => {
    if (layers) return;

    // light: default conf
    /*
    const dark = window.L.tileLayer('http://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      subdomains: ['a','b','c']
    })
    */
    const light = window.L.tileLayer(cartoBasemapUrl("light_all"), {
      subdomains:"abcd",
      attribution:'&copy; <a target="_blank" href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>, &copy; <a href="https://carto.com/attributions" target="_blank">CARTO</a>'
    })
    const dark = window.L.tileLayer("https://maps.zoomcharts.com/{z}/{x}/{y}.png", {
      subdomains:"abcd",
      attribution:'&copy; <a target="_blank" href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>, &copy; <a href="https://cartodb.com/attributions" target="_blank">CartoDB</a>'
    })

    layers = { light, dark };
  }

  return (chart, new_theme) => {
    const map = chart.leaflet();

    // the chart is still in progress of loading Leaflet.
    if (!map) return;

    ensureLayers();

    // initialization done, nothing to do
    if (current_theme && !new_theme) return;
    // no need to update
    if (current_theme === new_theme) return;

    const old_theme = current_theme;
    current_theme = current_theme === null ? initial_theme : new_theme;

    // console.log("OLD: ", old_theme, " - new: ", current_theme);

    if (old_theme) {
      map.removeLayer(layers[old_theme]);
    }

    map.addLayer(layers[current_theme]);
  }
}

function aggregateBySubroots(nodes) {
  let subroots = {};

  // populate w/ subroots
  nodes.filter(x => x.root && !x.subroot).forEach(x => {
    subroots[x.id] = {...x, aggs: []}
  })

  // aggregate leaves inside subroots
  nodes.filter(x => x.subroot).forEach(x => {
    subroots[x.subroot].aggs.push(x)
  })

  // add roots to it
  nodes.filter(x => !x.root).forEach(x => {
    subroots[x.id] = {...x, aggs: []};
  })

  return Object.values(subroots);
}

function aggregateByRoots(nodes) {
  let roots = {};

  // populate w/ roots
  nodes.filter(x => !x.root).forEach(x => {
    roots[x.id] = {...x, aggs: []}
  })

  // aggregates inside roots
  nodes.filter(x => x.root).forEach(x => {
    roots[x.root].aggs.push(x)
  })

  return Object.values(roots);
}


// taken from: https://jsfiddle.net/api/post/jquery/1.12/?example=auto-aggregation-and-scaling-by-the-rank-of-the-currently-visible-nodes.
function autoScaleNodes(chart, nodes, links) {
  // auto switch-case for returning radius (0.1 for smallest 10% or results etc.)
  var sizeSteps = [0.1, 5, 18, 21, 25, 30, 32, 34, 36, 52];
  // return defaults for faulty code (without initial autoscaling)
  if (!chart) {
      return { modifiedNodes: [], modifiedLinks: [] };
  }

  // get viewport bounds
  var visibleBounds = chart.bounds();
  var visibleNodes = [];
  // cycle through all nodes and outputing only those within bounds range
  for (var i = 0; i < nodes.length; i++) {
      var node = nodes[i];
      var r = 0;
      var c = node.data.coordinates;
      if (c && !node.removed
              && c[0] + r > visibleBounds.west
              && c[0] - r < visibleBounds.east
              && c[1] + r > visibleBounds.south
              && c[1] - r < visibleBounds.north) {
          visibleNodes.push(node);
      }
  }
  // return defaults, if there are no nodes visible
  if (visibleNodes.length === 0) {
      return { modifiedNodes: [], modifiedLinks: [] };
  }
  // return nodes without links, if there is only one node visible
  if (visibleNodes.length === 1) {
      visibleNodes[0].radius = sizeSteps[sizeSteps.length - 1] * 2;
      return { modifiedNodes: visibleNodes, modifiedLinks: [] };
  }
  // sort output nodes by aggregated weight (number earlier defined)
  visibleNodes.sort(function (a, b) {
      return a.data.weight - b.data.weight;
  });
  // make node radius bigger, by removing smallest values from sizeSteps array
  // if there is small variety of node values
  if (visibleNodes.length < sizeSteps.length) {
      sizeSteps.splice(0, sizeSteps.length - visibleNodes.length);
  }
  var count = visibleNodes.length;
  var stepSize = count / (sizeSteps.length - 1);
  // cycle through all result nodes and calculate new radius value
  for (var j = 0; j < count; j++) {
      var s = Math.floor(j / stepSize);

      visibleNodes[j].radius = sizeSteps[s + 1];
  }

  return { modifiedNodes: visibleNodes, modifiedLinks: [] };
}


// aggregate_range is a structured map w/ backoffice setting null value automatically
// in case its empty.
function aggregateRangeSettings(agg_range = {}, default_agg_range) {
  if (["level0", "level1", "level2"].every(k => agg_range[k] == null))
     agg_range = {...default_agg_range};

  // ensure correct type (list - even if null is accepted)
  ["level0", "level1", "level2"].forEach(k => {
    if (agg_range[k] == null) { agg_range[k] = []; }
  })

  return agg_range;
}


function getNodeProperties(node) {
  if (node.data.shapeFeature) {
    return node.data.shapeFeature.properties;
  }
  return node.data;
}

function upgradeSettingLayers(layers) {
  // https://zoomcharts.com/developers/en/net-chart/examples/style/label-style.html
  // https://zoomcharts.com/developers/en/net-chart/api-reference/settings/style.html

  // will be merged w/ layer setting
  function apply_attrs_to_node_or_link(n, attrs) {
    Object.keys(attrs || {}).forEach((k) => {
      n[k] = attrs[k];
    })
  }


  const nodeStyleFunction = function(n) {
    // works for items
    let props = getNodeProperties(n);

    apply_attrs_to_node_or_link(n, props.style || {});

    if (props.link) {
      n.cursor = "pointer";
    }
    
    // n.items is set directly onto the layer
  }

  function linkStyleFunction(n) {
    return nodeStyleFunction(n)
  }

  return layers.slice().map(l => {
    // update style by adding nodeStyleFunction
    let style = l.style || {};
    l.style = {...style, nodeStyleFunction, linkStyleFunction};

    return l;
  })
}

// click on data shape
function handleClick(node, router) {
  const props = getNodeProperties(node);


  if (props.link) {
    const [reticular, type, id] = props.link.split(":");

    let url;
    if (type === "post") {
      // url = "/desktop/post/" + id
      router.navigate('post', {id});
      return;
    } else if (["entity", "theme", "contact"].includes(type)) {
      // url = `/desktop/explore/${type}/${id}`
      router.navigate(`explore.${type}`, {id});
      return;
    }

    if (url) {
      window.location.href = url
    } else {
      console.log("BAD URL:", url)
    }
  }
}

// popup
// https://zoomcharts.com/developers/en/net-chart/examples/info-popup/custom-popup-on-click.htm
function buildUpdator(container) {
  let hover_node = null, dimensions = null, diff = null;
  let _chart = null;

  const el = document.createElement("div");
  el.classList.add("map-popup");

  const hide = () => {
    el.style.display = 'none'
    // _chart && _chart.leaflet().closePopup();
  } ;
  const show = () => { el.style.display = 'block' };
  const setHTML = (text) => {

    const html = marked.parse(text);
    const sanitized = sanitize(html, { USE_PROFILES: { html: true } });

    el.innerHTML = sanitized;
  }

  hide();

  // add this space to prevent mouse pointer to collide w/ the tooltip
  // thus triggering an hover again...
  const extra_space = 10;

  const updateDimensions = () => {
    el.style.top = dimensions.y + extra_space + "px";
    el.style.left = dimensions.x + extra_space + "px";
  };

  return {
    setChart: (chart) => {
      _chart = chart;
      window._chart = chart;
      container.appendChild(el);
    },
    onHoverChange: (e) => {
      const hover_element = e.hoverNode || e.hoverLink;
      const { force_text } = e;

      if (!hover_element) {
	hover_node = null;
	hide();
	return;
      }

      /*
      // not sure why you would need to hide on default... anyway, if nothing to show, it will be hidden
      if (hover_element.layer.layerSettings.id == "default") {
	hover_node = null;
	hide();
	return;
      }
      */
      const {hover: hover_text} = force_text ? {hover: force_text} : getNodeProperties(hover_element);

      if (!hover_text) {
	hover_node = null;
	hide();
	return
      }
      // format?
      hover_node = hover_element;
      setHTML(hover_text);

      // leaflet
      // const latlng = _chart.leaflet().layerPointToLatLng([e.x, e.y])
      // _chart.leaflet().openPopup(hover_text, latlng);

      dimensions = {x: e.x, y: e.y};

      // maintain this distance
      const dim = _chart.getNodeDimensions(hover_node);
      diff = {x: dim.x - e.x, y: dim.y - e.y};

      updateDimensions();
      show();
    },
    update(e, args) {
      if (! hover_node) {
	return;
      }

      const dim = _chart.getNodeDimensions(hover_node);

      dimensions = {
	x: dim.x + diff.x,
	y: dim.y + diff.y
      };
      updateDimensions();
    }
  }
}

export {
  aggregateByRoots,
  aggregateBySubroots,
  autoScaleNodes,
  loadThemedTiledLayer,
  aggregateRangeSettings,
  upgradeSettingLayers,
  handleClick,
  buildUpdator,
}
